import { useCallback, useEffect, useRef, useState } from "react";
import { useBlocker, useNavigate, useParams } from "react-router-dom";
import { Check, X } from "lucide-react";
import type { UpdateOnboardingInput } from "@school-sales-ai/shared";
import { useClient } from "@/hooks/useClients";
import { useClientsPath } from "@/hooks/useClientsPath";
import { useOnboardingPatchMutation, useOnboardingQuery } from "@/hooks/useOnboarding";
import { useClientSwitcherStore } from "@/stores/client-switcher.store";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StepActivate } from "./StepActivate";
import { StepCatalog } from "./StepCatalog";
import { StepClient } from "./StepClient";
import { StepContext } from "./StepContext";
import { StepKnowledge } from "./StepKnowledge";
import { StepManual } from "./StepManual";
import {
  FIRST_STEP,
  LAST_STEP,
  NEW_CLIENT_PARAM,
  WIZARD_STEPS,
  clampStep,
  wizardFamily,
} from "./steps";

/**
 * The client onboarding wizard — full screen, outside the CRM Layout.
 *
 * Full screen is a safety property, not a style choice: the sidebar carries the
 * client switcher, and the operator changing clients halfway through would silently
 * repoint every tenant-scoped write in the remaining steps.
 *
 * Nothing here holds a draft of its own. Each step writes its real artifact through
 * the same endpoint the maintenance CRM uses, then records the step. Leaving mid-flow
 * loses nothing but the current step's unsaved edits.
 */
export function OnboardingWizard(): React.JSX.Element {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  // Super-admins and account managers both run this wizard, and "Clientes" is a
  // different route for each of them.
  const clientsPath = useClientsPath();

  // `/onboarding/new` is step 1 for a client that does not exist yet. There is nothing
  // to fetch, no tenant to pin and no onboarding row until it is created.
  const isNew = clientId === NEW_CLIENT_PARAM;

  // `GET /super-admin/clients/:id` takes the id in the path, so it is safe to call
  // before the tenant is pinned — that is how we learn the name to pin it with.
  const {
    data: client,
    isPending: clientPending,
    isError,
  } = useClient(isNew ? undefined : clientId);
  const activeClientId = useClientSwitcherStore((s) => s.activeClientId);

  const { data: state } = useOnboardingQuery(
    isNew ? undefined : tenantReadyId(activeClientId, clientId),
  );
  const patch = useOnboardingPatchMutation(clientId);

  const [step, setStep] = useState<number | null>(null);

  /**
   * Warn before leaving the wizard with edits the current step has not saved. Only
   * in-app navigation is blocked (Salir); moving between steps is local state, and the
   * operator can see what they typed while doing it.
   *
   * A ref read by a predicate, not a boolean prop: with `useBlocker(stepDirty)` the
   * wizard blocked ITS OWN navigation. Clearing the flag and navigating happen in the
   * same tick — React batches, so the blocker still saw `true` and popped "¿Salir sin
   * guardar?" on top of a successful save. A predicate is evaluated at navigation time,
   * so it reads what was just written.
   */
  const stepDirtyRef = useRef(false);
  const setStepDirty = useCallback((dirty: boolean): void => {
    stepDirtyRef.current = dirty;
  }, []);
  const blocker = useBlocker(useCallback(() => stepDirtyRef.current, []));

  // ── The tenant gate ────────────────────────────────────────────────────────────
  // Every tenant-scoped request in this wizard is routed by the `X-Client-Id` header,
  // which api.ts reads from a localStorage-PERSISTED store with nothing tying it to
  // the URL. A stale value would make step 3 save this client's catalog into another
  // tenant and return 200, with no error to notice. So: pin it, keep it pinned (a
  // second tab sharing the store can move it), and render no step until it matches.
  const restoreTo = useRef<{ id: string | null; name: string | null } | null>(null);

  useEffect(() => {
    const { activeClientId: id, activeClientName: name } =
      useClientSwitcherStore.getState();
    restoreTo.current = { id, name };
    return () => {
      const previous = restoreTo.current;
      const store = useClientSwitcherStore.getState();
      if (previous?.id && previous.name) {
        store.setActiveClient(previous.id, previous.name);
      } else {
        store.clearActiveClient();
      }
    };
  }, []);

  useEffect(() => {
    if (!client || activeClientId === client.id) return;
    useClientSwitcherStore.getState().setActiveClient(client.id, client.name);
  }, [client, activeClientId]);

  // Resume where the operator left off, once and only once — after that the local
  // step wins, so advancing does not fight the server value.
  useEffect(() => {
    if (state && step === null) setStep(clampStep(state.currentStep));
  }, [state, step]);

  if (isError) {
    return (
      <CenteredMessage>
        <p className="text-sm text-red-500">No se pudo cargar el cliente.</p>
        <BackToClients onClick={() => navigate(clientsPath)} />
      </CenteredMessage>
    );
  }

  // Creating: the same frame, but only step 1 exists to show. On success the URL is
  // replaced with the real id, which turns this into the normal resumable flow at
  // step 2 — the creation hook has already pinned the tenant and recorded step 1.
  if (isNew) {
    return (
      <WizardFrame
        title="Nuevo cliente"
        step={FIRST_STEP}
        completedSteps={new Set()}
        onExit={() => navigate(clientsPath)}
        blocker={blocker}
      >
        <StepClient
          client={null}
          onDone={() => undefined}
          onCreated={(created) => {
            setStepDirty(false);
            navigate(`/super-admin/onboarding/${created.id}`, { replace: true });
          }}
          onDirtyChange={setStepDirty}
        />
      </WizardFrame>
    );
  }

  const tenantReady = Boolean(client) && activeClientId === client?.id;
  if (clientPending || !client || !tenantReady || step === null || !state) {
    return (
      <CenteredMessage>
        <span className="text-sm text-ink-500">Preparando el onboarding…</span>
      </CenteredMessage>
    );
  }

  const done = new Set(state.completedSteps);
  // Whether the wizard's copy speaks of a school or a business.
  const family = wizardFamily(client);

  function goTo(next: number): void {
    setStepDirty(false); // the new step starts clean
    setStep(clampStep(next));
    // Remember the position so closing the wizard resumes here.
    patch.mutate({ currentStep: clampStep(next) });
  }

  /** A step finished its work: record it, then move on. */
  function completeAndAdvance(n: number, extra: UpdateOnboardingInput = {}): void {
    const next = clampStep(n + 1);
    setStepDirty(false); // whatever was dirty has just been persisted
    setStep(next);
    patch.mutate({ ...extra, completedStep: n, currentStep: next });
  }

  const back = step > FIRST_STEP ? () => goTo(step - 1) : null;

  return (
    <WizardFrame
      title={client.name}
      step={step}
      completedSteps={done}
      onExit={() => navigate(clientsPath)}
      onStepClick={goTo}
      blocker={blocker}
    >
      {step === 1 && (
        <StepClient
          client={client}
          onDone={() => completeAndAdvance(1)}
          onCreated={() => undefined}
          onDirtyChange={setStepDirty}
        />
      )}

      {step === 2 && (
        <StepContext
          context={state.context}
          family={family}
          onBack={back}
          isBusy={patch.isPending}
          onDone={(context) => completeAndAdvance(2, { context })}
          // Recorded as done: the material is raw input, not an artifact, and an
          // undone step 2 would keep the onboarding from ever completing. Whatever
          // was saved before is kept — no `context` is sent.
          onSkip={() => completeAndAdvance(2)}
          onDirtyChange={setStepDirty}
        />
      )}

      {step === 3 && (
        <StepCatalog
          clientType={client.clientType}
          family={family}
          onBack={back}
          onDone={() => completeAndAdvance(3)}
          onDirtyChange={setStepDirty}
        />
      )}

      {step === 4 && (
        <StepKnowledge
          clientId={client.id}
          hasContext={Boolean(state.context?.raw?.trim())}
          onBack={back}
          onDone={() => completeAndAdvance(4)}
          onDirtyChange={setStepDirty}
          isBusy={patch.isPending}
        />
      )}

      {step > 4 && step < LAST_STEP && (
        <StepManual
          step={step}
          clientId={client.id}
          clientType={client.clientType}
          onBack={back}
          onDone={() => completeAndAdvance(step)}
          onSkip={() => goTo(step + 1)}
          isBusy={patch.isPending}
        />
      )}

      {step === LAST_STEP && (
        <StepActivate
          client={client}
          onBack={back}
          onDone={() => {
            // Records step 7, which seals `completed_at` once 1–7 are all done.
            patch.mutate(
              { completedStep: LAST_STEP, currentStep: LAST_STEP },
              { onSuccess: () => navigate(clientsPath) },
            );
          }}
        />
      )}
    </WizardFrame>
  );
}

type Blocker = ReturnType<typeof useBlocker>;

/**
 * Header, stepper and scroll area. Shared by the create branch (which has one step and
 * no navigation) and the resumable one, so both look like the same wizard.
 */
function WizardFrame({
  title,
  step,
  completedSteps,
  onExit,
  onStepClick,
  blocker,
  children,
}: {
  title: string;
  step: number;
  completedSteps: Set<number>;
  onExit: () => void;
  onStepClick?: (step: number) => void;
  blocker: Blocker;
  children: React.ReactNode;
}): React.JSX.Element {
  const current = WIZARD_STEPS.find((s) => s.n === step) ?? WIZARD_STEPS[0]!;

  return (
    <div className="flex h-screen flex-col bg-ink-050">
      <header className="flex flex-wrap items-center gap-3 border-b border-ink-200 bg-white px-6 py-3">
        <div className="mr-auto">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">
            Onboarding
          </p>
          <h1 className="text-lg font-semibold text-ink-900">{title}</h1>
        </div>
        <button
          type="button"
          onClick={onExit}
          className="flex items-center gap-1.5 rounded-lg border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-050"
        >
          <X size={14} />
          Salir
        </button>
      </header>

      <nav className="flex flex-wrap gap-2 border-b border-ink-200 bg-white px-6 py-3">
        {WIZARD_STEPS.map((s) => {
          const isDone = completedSteps.has(s.n);
          const isCurrent = s.n === step;
          return (
            <button
              key={s.n}
              type="button"
              // Only completed steps are reachable by clicking: jumping ahead would
              // land on a step whose inputs (the catalog, the KB) are not there yet.
              disabled={!onStepClick || (!isDone && !isCurrent)}
              onClick={() => onStepClick?.(s.n)}
              className={cn(
                "flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors",
                isCurrent
                  ? "bg-lime text-moss"
                  : isDone
                    ? "bg-ink-100 text-ink-700 hover:bg-ink-200"
                    : "bg-ink-050 text-ink-400",
              )}
            >
              {isDone ? <Check size={12} /> : <span>{s.n}</span>}
              {s.short}
            </button>
          );
        })}
      </nav>

      <main className="flex-1 overflow-y-auto px-6 py-6">
        <div className="mx-auto flex max-w-3xl flex-col gap-4">
          <h2 className="text-base font-semibold text-ink-900">{current.title}</h2>
          {children}
        </div>
      </main>

      {/* Only the current step's unsaved edits are at risk: everything already
          saved went to its real endpoint, not to wizard-local state. */}
      <Dialog
        open={blocker.state === "blocked"}
        onOpenChange={(open) => {
          if (!open) blocker.reset?.();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Salir sin guardar?</DialogTitle>
            <DialogDescription>
              Tienes cambios sin guardar en este paso. Si sales ahora, se perderán —
              lo que ya guardaste en pasos anteriores se conserva.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              onClick={() => blocker.reset?.()}
              className="rounded-lg border border-ink-200 px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-050"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => blocker.proceed?.()}
              className="rounded-lg bg-lime px-4 py-2 text-sm font-medium text-moss hover:bg-lime-dk"
            >
              Salir de todas formas
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * The onboarding query must not fire until the tenant is pinned. It is addressed by
 * path param, so it would not be misrouted — but a request that races the gate would
 * teach the next reader that firing early is fine.
 */
function tenantReadyId(
  activeClientId: string | null,
  clientId: string | undefined,
): string | undefined {
  return clientId && activeClientId === clientId ? clientId : undefined;
}

function CenteredMessage({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3 bg-ink-050">
      {children}
    </div>
  );
}

function BackToClients({ onClick }: { onClick: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-050"
    >
      Volver a Clientes
    </button>
  );
}
